import React, { useEffect, useMemo, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ListIcon, LayoutGridIcon, PhoneIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, UserIcon, MapPinIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { StatsCard } from '../components/ui/StatsCard';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
// ── TYPES ────────────────────────────────────────────────────────────────────
type VisitType = 'visita_venta' | 'visita_cobro' | 'reactivacion' | 'seguimiento';
type VisitStatus = 'pendiente' | 'visitado' | 'reprogramado' | 'codificacion';
type TeleventasResult = 'no_contesto' | 'promesa_pago' | 'venta_realizada' | 'seguimiento';
interface PlanEntry {
  id: string;
  fecha: Date;
  diaSemana: number;
  clienteNombre: string;
  codigoCliente: string;
  zona: string;
  vendedorId: string;
  vendedorNombre: string;
  tipoGestion: VisitType;
  estado: VisitStatus;
  hora?: string;
}
interface TeleventasEntry {
  id: string;
  clienteNombre: string;
  codigoCliente: string;
  fecha: Date;
  resultado: TeleventasResult;
  agente: string;
  notas?: string;
}
// ── HELPERS ──────────────────────────────────────────────────────────────────
const getMonday = (d: Date): Date => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
};
const getDayName = (d: Date): string => {
  const names = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return names[d.getDay()];
};
const getVisitTypeColor = (type: VisitType) => {
  switch (type) {
    case 'visita_venta':
      return 'bg-blue-600 text-white border-blue-700';
    case 'visita_cobro':
      return 'bg-emerald-600 text-white border-emerald-700';
    case 'reactivacion':
      return 'bg-amber-600 text-white border-amber-700';
    case 'seguimiento':
      return 'bg-violet-600 text-white border-violet-700';
    default:
      return 'bg-slate-600 text-white';
  }
};
const getVisitTypeLabel = (type: VisitType) => {
  switch (type) {
    case 'visita_venta':
      return 'Visita de Venta';
    case 'visita_cobro':
      return 'Cobranza';
    case 'reactivacion':
      return 'Reactivación';
    case 'seguimiento':
      return 'Seguimiento';
    default:
      return type;
  }
};
const getStatusBadgeVariant = (status: VisitStatus) => {
  switch (status) {
    case 'visitado':
      return 'success';
    case 'reprogramado':
      return 'danger';
    case 'codificacion':
      return 'info';
    default:
      return 'warning';
  }
};
const getStatusLabel = (status: VisitStatus) => {
  switch (status) {
    case 'pendiente':
      return 'pendiente';
    case 'visitado':
      return 'completada';
    case 'reprogramado':
      return 'cancelada';
    case 'codificacion':
      return 'codificación';
    default:
      return status;
  }
};
const nextStatus = (current: VisitStatus): VisitStatus => {
  switch (current) {
    case 'pendiente':
      return 'visitado';
    case 'visitado':
      return 'reprogramado';
    case 'reprogramado':
      return 'pendiente';
    default:
      return 'pendiente';
  }
};
const getTeleventasResultBadge = (result: TeleventasResult) => {
  switch (result) {
    case 'venta_realizada':
      return <Badge variant="success" data-id="element-2690">Venta Realizada</Badge>;
    case 'promesa_pago':
      return <Badge variant="warning" data-id="element-2691">Promesa Pago</Badge>;
    case 'no_contesto':
      return <Badge variant="danger" data-id="element-2692">No Contestó</Badge>;
    case 'seguimiento':
      return <Badge variant="info" data-id="element-2693">Seguimiento</Badge>;
  }
};
// ── COMPONENT ────────────────────────────────────────────────────────────────
export function PlanificacionPage() {
  const {
    user
  } = useAuth();
  const {
    clients,
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [activeTab, setActiveTab] = useState<'planificacion' | 'televentas' | 'resumen'>('planificacion');
  const [planEntries, setPlanEntries] = useState<PlanEntry[]>([]);
  const [televentasEntries, setTeleventasEntries] = useState<TeleventasEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  // Form state — fecha stored as string "YYYY-MM-DD" to avoid timezone issues
  const [planFormDate, setPlanFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [planFormCliente, setPlanFormCliente] = useState('');
  const [planFormCodigo, setPlanFormCodigo] = useState('');
  const [planFormZona, setPlanFormZona] = useState('');
  const [planFormTipo, setPlanFormTipo] = useState<VisitType>('visita_venta');
  const [planFormVendedorId, setPlanFormVendedorId] = useState('');
  const [planFormVendedorNombre, setPlanFormVendedorNombre] = useState('');
  const [callFormCliente, setCallFormCliente] = useState('');
  const [callFormCodigo, setCallFormCodigo] = useState('');
  const [callFormResultado, setCallFormResultado] = useState<TeleventasResult>('seguimiento');
  const [callFormNotas, setCallFormNotas] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const PLAN_VISIBLE_LIMIT = 4;
  // ── SUPABASE LOAD ──────────────────────────────────────────────────────────
  const mapPlanRow = (row: any): PlanEntry => ({
    id: row.id,
    fecha: new Date(row.fecha),
    diaSemana: new Date(row.fecha).getDay(),
    clienteNombre: row.cliente_nombre,
    codigoCliente: row.codigo_cliente || '',
    zona: row.zona || '',
    vendedorId: row.vendedor_id || '',
    vendedorNombre: row.vendedor_nombre || '',
    tipoGestion: row.tipo_gestion as VisitType,
    estado: row.estado as VisitStatus,
    hora: new Date(row.fecha).toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  });
  const loadPlanEntries = async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('plan_entries').select('*').order('fecha', {
        ascending: true
      });
      if (error) {
        console.error('Error loading plan_entries:', error);
        return;
      }
      if (data) setPlanEntries(data.map(mapPlanRow));
    } catch (err) {
      console.error('Error loading plan entries:', err);
    }
  };
  const loadTeleventasEntries = async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('televentas_entries').select('*').order('fecha', {
        ascending: false
      }).limit(50);
      if (error) {
        console.error('Error loading televentas:', error);
        return;
      }
      if (data) {
        setTeleventasEntries(data.map((row: any): TeleventasEntry => ({
          id: row.id,
          clienteNombre: row.cliente_nombre,
          codigoCliente: row.codigo_cliente || '',
          fecha: new Date(row.fecha),
          resultado: row.resultado as TeleventasResult,
          agente: row.agente,
          notas: row.notas || undefined
        })));
      }
    } catch (err) {
      console.error('Error loading televentas:', err);
    }
  };
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([loadPlanEntries(), loadTeleventasEntries()]);
      setIsLoading(false);
    };
    init();
    const planChannel = supabaseAdmin.channel('plan-entries-rt').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'plan_entries'
    }, () => loadPlanEntries()).subscribe();
    const teleChannel = supabaseAdmin.channel('televentas-rt').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'televentas_entries'
    }, () => loadTeleventasEntries()).subscribe();
    return () => {
      supabaseAdmin.removeChannel(planChannel);
      supabaseAdmin.removeChannel(teleChannel);
    };
  }, []);
  // ── COMPUTED ───────────────────────────────────────────────────────────────
  const startOfWeek = useMemo(() => getMonday(currentDate), [currentDate]);
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  }, [startOfWeek]);
  const weekRangeLabel = useMemo(() => {
    const end = new Date(startOfWeek);
    end.setDate(startOfWeek.getDate() + 4);
    return `Semana del ${startOfWeek.getDate()} al ${end.getDate()} de ${startOfWeek.toLocaleString('es-DO', {
      month: 'short'
    })}`;
  }, [startOfWeek]);
  const currentWeekEntries = useMemo(() => {
    const start = new Date(startOfWeek);
    start.setHours(0, 0, 0, 0);
    const end = new Date(startOfWeek);
    end.setDate(startOfWeek.getDate() + 5);
    end.setHours(0, 0, 0, 0);
    return planEntries.filter(e => {
      const d = new Date(e.fecha);
      return d >= start && d < end;
    });
  }, [planEntries, startOfWeek]);
  const stats = useMemo(() => ({
    programadas: currentWeekEntries.length,
    cobros: currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro' && e.estado === 'pendiente').length,
    reactivaciones: currentWeekEntries.filter(e => e.tipoGestion === 'reactivacion').length,
    disponibles: Math.max(0, clients.length - currentWeekEntries.length)
  }), [currentWeekEntries, clients]);
  // ── HANDLERS ───────────────────────────────────────────────────────────────
  const handlePrevWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };
  const handleNextWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };
  const resetPlanForm = () => {
    setPlanFormDate(new Date().toISOString().split('T')[0]);
    setPlanFormCliente('');
    setPlanFormCodigo('');
    setPlanFormZona('');
    setPlanFormTipo('visita_venta');
    setPlanFormVendedorId('');
    setPlanFormVendedorNombre('');
    setSaveError('');
  };
  const resetCallForm = () => {
    setCallFormCliente('');
    setCallFormCodigo('');
    setCallFormResultado('seguimiento');
    setCallFormNotas('');
    setSaveError('');
  };
  const handleToggleStatus = async (entry: PlanEntry) => {
    const newStatus = nextStatus(entry.estado);
    setUpdatingStatusId(entry.id);
    try {
      const {
        error
      } = await supabaseAdmin.from('plan_entries').update({
        estado: newStatus
      }).eq('id', entry.id);
      if (!error) {
        setPlanEntries(prev => prev.map(e => e.id === entry.id ? {
          ...e,
          estado: newStatus
        } : e));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };
  const handleSavePlan = async () => {
    if (!planFormCliente || !planFormDate) return;
    setSaveError('');
    setIsSaving(true);
    try {
      // Build date with noon time to avoid timezone edge cases
      const fecha = new Date(planFormDate + 'T10:00:00');
      const monday = getMonday(fecha);
      // Determine vendedor — use form selection or current user
      const vendedorId = planFormVendedorId || user?.id || null;
      const vendedorNombre = planFormVendedorNombre || user?.nombre || 'Usuario';
      const payload: Record<string, any> = {
        fecha: fecha.toISOString(),
        dia_semana: getDayName(fecha),
        cliente_nombre: planFormCliente,
        codigo_cliente: planFormCodigo || '',
        zona: planFormZona || 'General',
        vendedor_nombre: vendedorNombre,
        tipo_gestion: planFormTipo,
        estado: 'pendiente',
        semana_inicio: monday.toISOString().split('T')[0]
      };
      // Only include vendedor_id if it's a valid value (avoid FK constraint errors)
      if (vendedorId) {
        payload.vendedor_id = vendedorId;
      }
      const {
        data,
        error
      } = await supabaseAdmin.from('plan_entries').insert(payload).select();
      if (error) {
        console.error('Supabase error:', error);
        setSaveError(`Error al guardar: ${error.message}`);
        return;
      }
      // Success — add to local state immediately and close modal
      if (data && data.length > 0) {
        setPlanEntries(prev => [...prev, mapPlanRow(data[0])]);
      }
      setIsPlanModalOpen(false);
      resetPlanForm();
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError('Error inesperado al guardar. Intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveCall = async () => {
    if (!callFormCliente) return;
    setSaveError('');
    setIsSaving(true);
    try {
      const payload: Record<string, any> = {
        cliente_nombre: callFormCliente,
        codigo_cliente: callFormCodigo || '',
        fecha: new Date().toISOString(),
        resultado: callFormResultado,
        agente: user?.nombre || 'Agente',
        notas: callFormNotas || ''
      };
      // Only include agente_id if valid
      if (user?.id) {
        payload.agente_id = user.id;
      }
      const {
        data,
        error
      } = await supabaseAdmin.from('televentas_entries').insert(payload).select();
      if (error) {
        console.error('Supabase error:', error);
        setSaveError(`Error al guardar: ${error.message}`);
        return;
      }
      if (data && data.length > 0) {
        const newEntry: TeleventasEntry = {
          id: data[0].id,
          clienteNombre: data[0].cliente_nombre,
          codigoCliente: data[0].codigo_cliente || '',
          fecha: new Date(data[0].fecha),
          resultado: data[0].resultado as TeleventasResult,
          agente: data[0].agente,
          notas: data[0].notas || undefined
        };
        setTeleventasEntries(prev => [newEntry, ...prev]);
      }
      setIsCallModalOpen(false);
      resetCallForm();
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError('Error inesperado al guardar. Intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  // ── RENDER ─────────────────────────────────────────────────────────────────
  return <div className="space-y-6" data-id="element-2694">
      <PageHeader title="Planificación Comercial" subtitle="Gestión de rutas y televentas" icon={<CalendarIcon className="w-6 h-6" data-id="element-2696" />} data-id="element-2695">
        <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-2697">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-1 shadow-sm" data-id="element-2698">
            <button onClick={handlePrevWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2699">
              <ChevronLeftIcon className="w-4 h-4 text-slate-500" data-id="element-2700" />
            </button>
            <span className="text-sm font-medium text-slate-700 min-w-[180px] text-center" data-id="element-2701">
              {weekRangeLabel}
            </span>
            <button onClick={handleNextWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2702">
              <ChevronRightIcon className="w-4 h-4 text-slate-500" data-id="element-2703" />
            </button>
          </div>
          <div className="bg-white border border-slate-200 p-1 rounded-xl flex items-center shadow-sm" data-id="element-2704">
            <button onClick={() => setViewMode('calendar')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'calendar' ? 'bg-[#eef2fc] text-[#2952C4]' : 'text-slate-500 hover:text-slate-700'}`} title="Vista Calendario" data-id="element-2705">
              <LayoutGridIcon className="w-4 h-4" data-id="element-2706" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#eef2fc] text-[#2952C4]' : 'text-slate-500 hover:text-slate-700'}`} title="Vista Lista" data-id="element-2707">
              <ListIcon className="w-4 h-4" data-id="element-2708" />
            </button>
          </div>
          <Button onClick={() => {
          resetPlanForm();
          setIsPlanModalOpen(true);
        }} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2710" />} data-id="element-2709">
            Nueva
          </Button>
        </div>
      </PageHeader>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-1" data-id="element-2711">
        <StatsCard title="Visitas Programadas" value={stats.programadas} icon={<MapPinIcon className="w-5 h-5" data-id="element-2713" />} variant="primary" data-id="element-2712" />
        <StatsCard title="Cobros Pendientes" value={stats.cobros} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-2715" />} variant="success" data-id="element-2714" />
        <StatsCard title="Reactivaciones" value={stats.reactivaciones} icon={<ClockIcon className="w-5 h-5" data-id="element-2717" />} variant="warning" data-id="element-2716" />
        <StatsCard title="Disp. Televentas" value={stats.disponibles} icon={<PhoneIcon className="w-5 h-5" data-id="element-2719" />} variant="default" data-id="element-2718" />
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start animate-stagger-2" data-id="element-2720">
        {/* LEFT PANEL */}
        <div className="lg:col-span-3 space-y-6" data-id="element-2721">
          {isLoading ? <Card data-id="element-2722">
              <div className="flex items-center justify-center py-16" data-id="element-2723">
                <div className="w-8 h-8 border-3 border-slate-200 border-t-[#2952C4] rounded-full animate-spin" data-id="element-2724" />
                <span className="ml-3 text-sm text-slate-500" data-id="element-2725">
                  Cargando planificación...
                </span>
              </div>
            </Card> : viewMode === 'calendar' ? <Card padding="none" className="overflow-hidden" data-id="element-2726">
              <div className="grid grid-cols-5 bg-[#0d1b46] text-white" data-id="element-2727">
                {weekDays.map((date, i) => <div key={i} className="py-3 px-2 text-center border-r border-white/10 last:border-0" data-id="element-2728">
                    <p className="text-xs font-medium opacity-70 uppercase" data-id="element-2729">
                      {date.toLocaleDateString('es-DO', {
                  weekday: 'short'
                })}
                    </p>
                    <p className="text-lg font-bold" data-id="element-2730">{date.getDate()}</p>
                  </div>)}
              </div>
              <div className="grid grid-cols-5 min-h-[400px] bg-slate-50" data-id="element-2731">
                {weekDays.map((date, i) => {
              const dayEntries = currentWeekEntries.filter(e => {
                const entryDate = new Date(e.fecha);
                return entryDate.getDate() === date.getDate() && entryDate.getMonth() === date.getMonth() && entryDate.getFullYear() === date.getFullYear();
              });
              return <div key={i} className="border-r border-slate-200 last:border-0 p-2 space-y-2" data-id="element-2732">
                      {dayEntries.map(entry => <div key={entry.id} className={`p-2 rounded-lg shadow-sm text-white text-xs ${getVisitTypeColor(entry.tipoGestion)}`} data-id="element-2733">
                          {entry.hora && <p className="opacity-80 mb-0.5 text-[10px]" data-id="element-2734">
                              {entry.hora}
                            </p>}
                          <p className="font-bold truncate" data-id="element-2735">
                            {entry.clienteNombre}
                          </p>
                          <p className="opacity-90 truncate mt-0.5" data-id="element-2736">
                            {getVisitTypeLabel(entry.tipoGestion)}
                          </p>
                        </div>)}
                      {dayEntries.length === 0 && <div className="border-2 border-dashed border-emerald-200 bg-emerald-50 rounded-lg p-2 text-center mt-4" data-id="element-2737">
                          <p className="text-[10px] font-medium text-emerald-600" data-id="element-2738">
                            Disponible
                          </p>
                        </div>}
                    </div>;
            })}
              </div>
              <div className="p-4 border-t border-slate-100 flex flex-wrap gap-4 bg-white" data-id="element-2739">
                <div className="flex items-center gap-2" data-id="element-2740">
                  <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-2741"></div>
                  <span className="text-xs text-slate-600" data-id="element-2742">
                    Visita de Venta
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2743">
                  <div className="w-3 h-3 rounded-full bg-emerald-600" data-id="element-2744"></div>
                  <span className="text-xs text-slate-600" data-id="element-2745">Cobranza</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2746">
                  <div className="w-3 h-3 rounded-full bg-amber-600" data-id="element-2747"></div>
                  <span className="text-xs text-slate-600" data-id="element-2748">Reactivación</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2749">
                  <div className="w-3 h-3 rounded-full bg-violet-600" data-id="element-2750"></div>
                  <span className="text-xs text-slate-600" data-id="element-2751">Seguimiento</span>
                </div>
              </div>
            </Card> : <Card padding="none" data-id="element-2752">
              <div className="overflow-x-auto" data-id="element-2753">
                <table className="w-full text-sm" data-id="element-2754">
                  <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2755">
                    <tr data-id="element-2756">
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-2757">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-2758">
                        RV
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-2759">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-500 font-heading" data-id="element-2760">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-500 font-heading" data-id="element-2761">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-2762">
                    {currentWeekEntries.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => <tr key={entry.id} className="hover:bg-slate-50" data-id="element-2763">
                          <td className="px-4 py-3" data-id="element-2764">
                            <p className="font-medium text-slate-900" data-id="element-2765">
                              {entry.clienteNombre}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-2766">
                              {entry.codigoCliente} • {entry.zona}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-slate-600" data-id="element-2767">
                            {entry.vendedorNombre}
                          </td>
                          <td className="px-4 py-3 text-slate-600" data-id="element-2768">
                            {entry.fecha.toLocaleDateString('es-DO', {
                      weekday: 'short',
                      day: 'numeric'
                    })}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-2769">
                            <Badge variant="default" className="text-xs" data-id="element-2770">
                              {getVisitTypeLabel(entry.tipoGestion)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-2771">
                            <button onClick={() => handleToggleStatus(entry)} disabled={updatingStatusId === entry.id} className="inline-flex items-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-50" title="Clic para cambiar estado" data-id="element-2772">
                              {updatingStatusId === entry.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-2773" /> : <Badge variant={getStatusBadgeVariant(entry.estado)} data-id="element-2774">
                                  {getStatusLabel(entry.estado)}
                                </Badge>}
                            </button>
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={currentPage} totalPages={Math.ceil(currentWeekEntries.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} totalItems={currentWeekEntries.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2775" />
            </Card>}
        </div>

        {/* RIGHT PANEL */}
        <div className="lg:col-span-2" data-id="element-2776">
          <Card padding="none" className="h-full flex flex-col" data-id="element-2777">
            <div className="flex border-b border-slate-200" data-id="element-2778">
              <button onClick={() => setActiveTab('planificacion')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'planificacion' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-2779">
                Planificación
              </button>
              <button onClick={() => setActiveTab('televentas')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'televentas' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-2780">
                Televentas
              </button>
              <button onClick={() => setActiveTab('resumen')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'resumen' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-2781">
                Resumen
              </button>
            </div>

            <div className="flex-1 p-4 overflow-y-auto max-h-[600px]" data-id="element-2782">
              {activeTab === 'planificacion' && <div className="space-y-4" data-id="element-2783">
                  <div className="relative" data-id="element-2784">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2785" />
                    <input type="text" placeholder="Buscar cliente..." value={planSearchQuery} onChange={e => setPlanSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-2786" />
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2" data-id="element-2787">
                    <span data-id="element-2788">Cliente</span>
                    <span data-id="element-2789">Estado Gestión</span>
                  </div>
                  {(() => {
                const filtered = currentWeekEntries.filter(entry => planSearchQuery ? entry.clienteNombre.toLowerCase().includes(planSearchQuery.toLowerCase()) || entry.vendedorNombre.toLowerCase().includes(planSearchQuery.toLowerCase()) || entry.zona.toLowerCase().includes(planSearchQuery.toLowerCase()) : true);
                const visible = planSearchQuery ? filtered : filtered.slice(0, PLAN_VISIBLE_LIMIT);
                const hiddenCount = filtered.length - visible.length;
                return <>
                        {visible.map(entry => <div key={entry.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-2790">
                            <div data-id="element-2791">
                              <p className="font-semibold text-slate-900 text-sm" data-id="element-2792">
                                {entry.clienteNombre}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2793">
                                {entry.vendedorNombre} •{' '}
                                {entry.fecha.toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short'
                        })}
                              </p>
                              <span className="inline-block mt-1.5 px-2 py-0.5 bg-white border border-slate-200 rounded text-[10px] text-slate-600 font-medium" data-id="element-2794">
                                {getVisitTypeLabel(entry.tipoGestion)}
                              </span>
                            </div>
                            <div className="flex flex-col items-end gap-1" data-id="element-2795">
                              <div className="flex items-center gap-1.5" data-id="element-2796">
                                <span className="text-[10px] font-medium text-rose-600" data-id="element-2797">
                                  En gestión por RV
                                </span>
                                <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" data-id="element-2798"></div>
                              </div>
                              <button onClick={() => handleToggleStatus(entry)} disabled={updatingStatusId === entry.id} className="inline-flex items-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 cursor-pointer" title="Clic para cambiar estado" data-id="element-2799">
                                {updatingStatusId === entry.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-2800" /> : <Badge variant={getStatusBadgeVariant(entry.estado)} size="sm" data-id="element-2801">
                                    {getStatusLabel(entry.estado)}
                                  </Badge>}
                              </button>
                            </div>
                          </div>)}
                        {hiddenCount > 0 && !planSearchQuery && <div className="text-center py-2" data-id="element-2802">
                            <p className="text-xs text-slate-400" data-id="element-2803">
                              +{hiddenCount} más — usa el buscador para
                              encontrarlos
                            </p>
                          </div>}
                        {filtered.length === 0 && <div className="text-center py-6" data-id="element-2804">
                            <SearchIcon className="w-6 h-6 text-slate-300 mx-auto mb-2" data-id="element-2805" />
                            <p className="text-sm text-slate-500" data-id="element-2806">
                              {planSearchQuery ? `No se encontraron resultados para "${planSearchQuery}"` : 'No hay planificaciones esta semana'}
                            </p>
                          </div>}
                      </>;
              })()}
                  <div className="pt-4 mt-4 border-t border-slate-100 grid grid-cols-2 gap-3" data-id="element-2807">
                    <div className="bg-[#2952C4] text-white p-3 rounded-lg text-center" data-id="element-2808">
                      <p className="text-xs opacity-80" data-id="element-2809">Visitas Programadas</p>
                      <p className="text-xl font-bold font-heading" data-id="element-2810">
                        {stats.programadas}
                      </p>
                    </div>
                    <div className="bg-emerald-600 text-white p-3 rounded-lg text-center" data-id="element-2811">
                      <p className="text-xs opacity-80" data-id="element-2812">Cobros Pendientes</p>
                      <p className="text-xl font-bold font-heading" data-id="element-2813">
                        {stats.cobros}
                      </p>
                    </div>
                  </div>
                </div>}

              {activeTab === 'televentas' && <div className="space-y-4" data-id="element-2814">
                  <Button className="w-full" size="sm" onClick={() => {
                resetCallForm();
                setIsCallModalOpen(true);
              }} leftIcon={<PhoneIcon className="w-4 h-4" data-id="element-2816" />} data-id="element-2815">
                    Registrar Llamada
                  </Button>
                  <div className="space-y-3" data-id="element-2817">
                    {televentasEntries.length === 0 && <div className="text-center py-8" data-id="element-2818">
                        <PhoneIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-2819" />
                        <p className="text-sm text-slate-500" data-id="element-2820">
                          No hay llamadas registradas
                        </p>
                      </div>}
                    {televentasEntries.map(entry => <div key={entry.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-2821">
                        <div className="flex justify-between items-start mb-2" data-id="element-2822">
                          <div data-id="element-2823">
                            <p className="font-semibold text-slate-900 text-sm" data-id="element-2824">
                              {entry.clienteNombre}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-2825">
                              {entry.codigoCliente}
                            </p>
                          </div>
                          {getTeleventasResultBadge(entry.resultado)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 border-t border-slate-200 pt-2 mt-2" data-id="element-2826">
                          <UserIcon className="w-3 h-3" data-id="element-2827" />
                          <span data-id="element-2828">{entry.agente}</span>
                          <span className="mx-1" data-id="element-2829">•</span>
                          <span data-id="element-2830">{entry.fecha.toLocaleDateString()}</span>
                        </div>
                        {entry.notas && <p className="text-xs text-slate-600 mt-2 bg-white p-2 rounded border border-slate-100 italic" data-id="element-2831">
                            "{entry.notas}"
                          </p>}
                      </div>)}
                  </div>
                </div>}

              {activeTab === 'resumen' && <div className="space-y-6" data-id="element-2832">
                  <div data-id="element-2833">
                    <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-2834">
                      Cumplimiento Semanal
                    </h4>
                    <div className="space-y-3" data-id="element-2835">
                      {(() => {
                    const visitados = currentWeekEntries.filter(e => e.estado === 'visitado').length;
                    const pctVisitas = currentWeekEntries.length ? Math.round(visitados / currentWeekEntries.length * 100) : 0;
                    const cobrosHechos = currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro' && e.estado === 'visitado').length;
                    const cobrosTotal = currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro').length;
                    const pctCobros = cobrosTotal ? Math.round(cobrosHechos / cobrosTotal * 100) : 0;
                    return <>
                            <div data-id="element-2836">
                              <div className="flex justify-between text-xs mb-1" data-id="element-2837">
                                <span data-id="element-2838">Visitas Realizadas</span>
                                <span className="font-bold" data-id="element-2839">{pctVisitas}%</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-2840">
                                <div className="bg-[#2952C4] h-2 rounded-full transition-all" style={{
                            width: `${pctVisitas}%`
                          }} data-id="element-2841"></div>
                              </div>
                            </div>
                            <div data-id="element-2842">
                              <div className="flex justify-between text-xs mb-1" data-id="element-2843">
                                <span data-id="element-2844">Cobros Efectivos</span>
                                <span className="font-bold" data-id="element-2845">{pctCobros}%</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-2846">
                                <div className="bg-emerald-600 h-2 rounded-full transition-all" style={{
                            width: `${pctCobros}%`
                          }} data-id="element-2847"></div>
                              </div>
                            </div>
                          </>;
                  })()}
                    </div>
                  </div>
                  <div data-id="element-2848">
                    <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-2849">
                      Desglose por Tipo
                    </h4>
                    <div className="space-y-2" data-id="element-2850">
                      {(['visita_venta', 'visita_cobro', 'reactivacion', 'seguimiento'] as VisitType[]).map(type => {
                    const count = currentWeekEntries.filter(e => e.tipoGestion === type).length;
                    const pct = currentWeekEntries.length ? Math.round(count / currentWeekEntries.length * 100) : 0;
                    return <div key={type} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded" data-id="element-2851">
                            <span className="font-medium text-slate-700" data-id="element-2852">
                              {getVisitTypeLabel(type)}
                            </span>
                            <div className="flex items-center gap-2" data-id="element-2853">
                              <span className="text-slate-500" data-id="element-2854">{count}</span>
                              <span className="font-bold w-8 text-right" data-id="element-2855">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>
                </div>}
            </div>
          </Card>
        </div>
      </div>

      {/* MODAL: Nueva Planificación */}
      <Modal isOpen={isPlanModalOpen} onClose={() => setIsPlanModalOpen(false)} title="Nueva Planificación" data-id="element-2856">
        <ModalBody data-id="element-2857">
          <div className="space-y-4" data-id="element-2858">
            {saveError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-2859">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-2860" />
                {saveError}
              </div>}
            <div className="grid grid-cols-2 gap-4" data-id="element-2861">
              <div data-id="element-2862">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2863">
                  Fecha
                </label>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormDate} onChange={e => setPlanFormDate(e.target.value)} data-id="element-2864" />
              </div>
              <div data-id="element-2865">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2866">
                  Tipo Gestión
                </label>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormTipo} onChange={e => setPlanFormTipo(e.target.value as VisitType)} data-id="element-2867">
                  <option value="visita_venta" data-id="element-2868">Visita de Venta</option>
                  <option value="visita_cobro" data-id="element-2869">Visita de Cobro</option>
                  <option value="reactivacion" data-id="element-2870">Reactivación</option>
                  <option value="seguimiento" data-id="element-2871">Seguimiento</option>
                </select>
              </div>
            </div>
            <div data-id="element-2872">
              <SearchableSelect label="Cliente" value={planFormCliente} onChange={val => {
              const client = clients.find(c => c.nombre === val);
              setPlanFormCliente(val);
              setPlanFormCodigo(client?.codigo || client?.id || '');
              setPlanFormZona(client?.zona || 'General');
            }} options={clients.map(c => c.nombre)} placeholder="Buscar cliente..." maxVisible={5} data-id="element-2873" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2874">
              <Input label="Código" value={planFormCodigo} readOnly className="bg-slate-50" data-id="element-2875" />
              <Input label="Zona" value={planFormZona} readOnly className="bg-slate-50" data-id="element-2876" />
            </div>
            {isAdmin && <div data-id="element-2877">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2878">
                  Vendedor
                </label>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormVendedorId} onChange={e => {
              const v = users.find(u => u.id === e.target.value);
              setPlanFormVendedorId(e.target.value);
              setPlanFormVendedorNombre(v?.nombre || '');
            }} data-id="element-2879">
                  <option value="" data-id="element-2880">Seleccionar vendedor...</option>
                  {users.filter(u => u.activo).map(u => <option key={u.id} value={u.id} data-id="element-2881">
                        {u.nombre}
                      </option>)}
                </select>
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2882">
          <Button variant="secondary" onClick={() => setIsPlanModalOpen(false)} data-id="element-2883">
            Cancelar
          </Button>
          <Button onClick={handleSavePlan} isLoading={isSaving} disabled={!planFormCliente || !planFormDate} data-id="element-2884">
            Guardar Planificación
          </Button>
        </ModalFooter>
      </Modal>

      {/* MODAL: Registrar Llamada */}
      <Modal isOpen={isCallModalOpen} onClose={() => setIsCallModalOpen(false)} title="Registrar Gestión Televentas" data-id="element-2885">
        <ModalBody data-id="element-2886">
          <div className="space-y-4" data-id="element-2887">
            {saveError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-2888">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-2889" />
                {saveError}
              </div>}
            <div data-id="element-2890">
              <SearchableSelect label="Cliente" value={callFormCliente} onChange={val => {
              const client = clients.find(c => c.nombre === val);
              setCallFormCliente(val);
              setCallFormCodigo(client?.id || '');
            }} options={clients.map(c => c.nombre)} placeholder="Buscar cliente..." maxVisible={5} data-id="element-2891" />
            </div>
            <div data-id="element-2892">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2893">
                Resultado
              </label>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={callFormResultado} onChange={e => setCallFormResultado(e.target.value as TeleventasResult)} data-id="element-2894">
                <option value="no_contesto" data-id="element-2895">No Contestó</option>
                <option value="promesa_pago" data-id="element-2896">Promesa de Pago</option>
                <option value="venta_realizada" data-id="element-2897">Venta Realizada</option>
                <option value="seguimiento" data-id="element-2898">Seguimiento</option>
              </select>
            </div>
            <div data-id="element-2899">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2900">
                Notas
              </label>
              <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none h-24 resize-none" placeholder="Detalles de la llamada..." value={callFormNotas} onChange={e => setCallFormNotas(e.target.value)} data-id="element-2901" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2902">
          <Button variant="secondary" onClick={() => setIsCallModalOpen(false)} data-id="element-2903">
            Cancelar
          </Button>
          <Button onClick={handleSaveCall} isLoading={isSaving} disabled={!callFormCliente} data-id="element-2904">
            Registrar Llamada
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}